import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { isNullish } from '@epicuro-next/utilities';

// no operation function, used when the keycode is not supported
const noop = () => {
  // comment to make linter silent
};

@Directive({
  selector: '[epcGridNavigation]',
  standalone: false,
})
export class GridNavigationDirective {
  /**
   * Handlers for key down events.
   */
  private handlersKeyDown: { [code: string]: () => void } = {
    ArrowRight: this.onArrowRight.bind(this),
    ArrowLeft: this.onArrowLeft.bind(this),
    ArrowUp: this.onArrowUp.bind(this),
    ArrowDown: this.onArrowDown.bind(this),
  };
  private readonly blurEvent = new CustomEvent('navigationBlur');
  private readonly focusEvent = new CustomEvent('navigationSelect');

  /**
   * Handlers for key up events.
   */
  private handlersKeyUp: { [code: string]: () => void } = {
    Tab: this.onTab.bind(this),
  };

  @Input()
  public selectionClass = 'selected';

  @Input()
  public rowSelector: string;

  @Input()
  public cellSelector: string;

  @Input()
  public disabledSelector = 'disabled';

  /** Currently selected row */
  private currentRow: number;
  private currentRowElement: HTMLElement;

  /** Currently focused / selected cell in a row */
  private currentCell: number;
  private currentCellElement: HTMLElement;

  constructor(private host: ElementRef) {}

  private get rows() {
    return this.host.nativeElement.querySelectorAll(this.rowSelector);
  }

  /**
   * Listens to key down events
   * @param event
   */
  @HostListener('keydown', ['$event'])
  public handleKeyDownPress(event: KeyboardEvent) {
    /*
    this is mostly needed to prevent Tab's default behavior that focuses on last focused input
    instead, launch custom logic of finding next available cell (see onTab method)
    */
    if (this.currentCellElement?.hasAttribute(this.disabledSelector)) {
      event.preventDefault();
    }
    (this.handlersKeyDown[event.code] ?? noop)();
  }

  /**
   * Listens to key down events
   * @param event
   */
  @HostListener('keyup', ['$event'])
  public handleKeyUpPress(event: KeyboardEvent) {
    (this.handlersKeyUp[event.code] ?? noop)();
  }

  /**
   * Listen to click events to update the currently selected cell
   * @param targetElement
   */
  @HostListener('focusin', ['$event.target'])
  public handleClick(targetElement: HTMLElement) {
    // FYI we can not properly handle click on input if we have jumping inventory overview row
    // take a look in the code of size-grid.component around input with cellSelector (navCell)
    // we have click and focus events, click event returns grid-body el, but focus event is triggered for an input
    // so focus event is patched with fake click on the input to push this listener work properly
    // it looks like an open window to a loop of events
    const cellEl = targetElement.closest<HTMLElement>(this.cellSelector);

    if (cellEl && cellEl !== this.currentCellElement) {
      const rowEl = cellEl.closest<HTMLElement>(this.rowSelector);
      if (rowEl) {
        if (this.currentCellElement) {
          this.currentCellElement.classList.toggle(this.selectionClass);
          this.currentCellElement.dispatchEvent(this.blurEvent);
        }
        cellEl.classList.toggle(this.selectionClass);
        this.currentCell = Array.from(
          rowEl?.querySelectorAll(this.cellSelector) ?? [],
        ).indexOf(cellEl);
        this.selectRow(Array.from(this.rows).indexOf(rowEl));
        this.currentCellElement = cellEl;
      }
    }
  }

  private onArrowLeft() {
    if (this.currentCellElement) {
      if (this.currentCell) {
        this.selectCell(this.currentCell - 1);
      }
    } else {
      this.selectFirstCell();
    }
  }

  private onArrowRight() {
    if (this.currentCellElement) {
      this.selectCell(this.currentCell + 1);
    } else {
      this.selectFirstCell();
    }
  }

  private onArrowUp() {
    if (this.currentCellElement) {
      const rows = this.rows;
      let nextRowIndex = this.currentRow - 1;

      while (
        rows[nextRowIndex] &&
        getComputedStyle(rows[nextRowIndex]).display === 'none'
      ) {
        nextRowIndex--;
      }

      if (rows[nextRowIndex] === undefined) return;

      this.currentRow = nextRowIndex;
      this.currentRowElement = rows[nextRowIndex];
      this.selectCell(this.currentCell);
    } else {
      this.selectFirstCell();
    }
  }

  private onArrowDown() {
    if (this.currentCellElement) {
      const rows = this.rows;
      let nextRowIndex = this.currentRow + 1;

      while (
        rows[nextRowIndex] &&
        getComputedStyle(rows[nextRowIndex]).display === 'none'
      ) {
        nextRowIndex++;
      }

      if (rows[nextRowIndex] === undefined) return;

      this.currentRow = nextRowIndex;
      this.currentRowElement = rows[nextRowIndex];
      this.selectCell(this.currentCell);
    } else {
      this.selectFirstCell();
    }
  }

  private onTab() {
    if (this.currentCellElement?.hasAttribute(this.disabledSelector)) {
      const nextCell = this.findNextAvailableCell(this.currentRow);

      if (nextCell) {
        this.selectRow(nextCell.row);
        this.selectCell(nextCell.cell);
      }
    }
  }

  /**
   * Selects the first available cell from the grid
   */
  private selectFirstCell() {
    const row = this.rows[0];
    if (!row) {
      return;
    }
    this.currentRowElement = row;
    this.currentRow = 0;
    this.selectCell(0, true);
  }

  /**
   * Selects a cell within the currently selected row given a column number.
   * @param column
   */
  private selectCell(column: number, skipDisabled = false) {
    const cells = this.currentRowElement.querySelectorAll<HTMLElement>(
      this.cellSelector,
    );
    const nextCell = skipDisabled
      ? cells[this.findNextEnabledCellInRow(cells, column) ?? 0]
      : cells[column];

    if (nextCell) {
      if (this.currentCellElement) {
        this.currentCellElement.dispatchEvent(this.blurEvent);
        this.currentCellElement.classList.toggle(this.selectionClass);
      }
      nextCell.classList.toggle(this.selectionClass);
      this.currentCellElement?.blur();
      this.currentCellElement = cells[column];
      this.currentCell = column;
      nextCell.focus();
      nextCell.dispatchEvent(this.focusEvent);
    }
  }

  private selectRow(rowIndex: number) {
    this.currentRow = rowIndex;
    this.currentRowElement = this.rows[rowIndex];
  }

  private findNextEnabledCellInRow(
    cells: NodeListOf<HTMLElement>,
    column: number,
  ): number | null {
    if (!cells[column]) {
      return null;
    }
    if (cells[column].hasAttribute(this.disabledSelector)) {
      const modifier = 1;
      return this.findNextEnabledCellInRow(cells, column + modifier);
    } else {
      return column;
    }
  }

  private findNextAvailableCell(rowIndex: number): {
    row: number;
    cell: number;
  } {
    const rowEl = this.rows[rowIndex];
    const cells = rowEl.querySelectorAll(this.cellSelector);

    // find next available cell in row
    // also check if function is already run against next row. if yes -> start looking from first cell
    let nextCell = this.findNextEnabledCellInRow(
      cells,
      rowIndex > this.currentRow ? 0 : this.currentCell,
    );
    if (isNullish(nextCell)) {
      // if found nothing -> check next row
      if (rowIndex + 1 < this.rows.length) {
        return this.findNextAvailableCell(rowIndex + 1);
      } else {
        // if checking already last row -> select first available cell
        nextCell = this.findNextEnabledCellInRow(cells, 0);
      }
    }
    return { row: rowIndex, cell: nextCell ?? 0 };
  }
}
